import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl, calculatePercentage, getPromotionMainColor, parseBool, getPromotionTextColor, isColorBright, isDesktop } from '../lib/ep.js';
import { useCSS, useSimpleFilter } from '../lib/hooks.js';
import md5 from 'md5';

import DefaultSportsShirt from '../components/svg/DefaultSportsShirt.js';

import ActionButton from '../components/builder/ActionButton.js';
import ActionLink from '../components/builder/ActionLink.js';
import ButtonCollection from '../components/builder/ButtonCollection.js';
import FloatingLayer from '../components/builder/FloatingLayer.js';
import SectionTitle from '../components/builder/SectionTitle.js';
import ShareButton from '../components/front/buttons/ShareButton.js';
import { localizeFields } from '../components/builder/DynamicFields.js';
import SaveButton from '../components/builder/SaveButton.js';

import LeveledHTMLText from '../components/front/helpers/LeveledHTMLText.js';
import FloatingLayerPublic from '../components/front/FloatingLayerPublic.js';

import { useTeamStats } from '../components/backend/TeamBuilder.js';

const default_player_style = 'none';

const sortPlayersInPosition = (team, order_type, stats) => {
	const sorted = {};
	if(team.sections == undefined || team.candidates == undefined) return sorted;

	team.sections.map((section) => {
		sorted[section.id] = team.candidates.filter((c) => ((section?.candidates ?? []).includes(c.id))).sort((a, b) => {
			if(order_type == 'id') return 0;

			const key_a = `${a.id}_${section.id}`;
			const key_b = `${b.id}_${section.id}`;

			const num_a = stats.team[key_a] ?? 0;
			const num_b = stats.team[key_b] ?? 0;

			return (parseFloat(num_a)) > (parseFloat(num_b)) ? -1 : 1;
		}).map((candidate) => {
			return {
				...candidate, percentage:calculatePercentage(parseInt(stats.team[`${candidate.id}_${section.id}`]), stats.total),
			};
		});
	});

	return sorted;
};

export const getSectionLimits = (section, return_as_values = false, intl = false) => {
	let min = parseInt(section?.min ?? '1');
	if(isNaN(min)) min = 1;
	
	let max = parseInt(section?.max ?? '1');
	if(isNaN(max)) max = 1;

	if(return_as_values) {
		return {
			min,
			max,
		};
	}

	const i18n = (m, c) => i18nIntl(m, intl, c);

	if(min === max) {
		if(parseInt(min) === 1) return '';

		return `(${i18n("Needed", 'lowercase')} ${min})`;
	}

	return `(${i18n("Min.", 'lowercase')} ${min} - ${i18n("Max.", 'lowercase')} ${max})`;
};

export const getPlayerDorsal = (player) => {
	const dorsal = player?.dorsal ?? "";
	if(dorsal != "") return dorsal;

	const player_name = player?.name ?? "Player";
	return player_name.toUpperCase().substring(0, 2);
};

const getDefaultTeamColors = (section, promotion) => {
	const main_promo_color = getPromotionMainColor(promotion);
	const is_main_promo_color_light = isColorBright(main_promo_color);

	return {
		'--candidate-bg-color':section?.color ?? main_promo_color,
		'--candidate-text-color':section?.text_color ?? `rgba(${(is_main_promo_color_light) ? '0, 0, 0' : '255, 255, 255'}, 1)`,
	};
};

export const formatPlayerName = (player, intl) => {
	const i18n = (m, c) => i18nIntl(m, intl, c);

	const name = player?.name ?? i18n("Player");
	const dorsal = getPlayerDorsal(player);

	if(dorsal !== null) return `${dorsal} - ${name}`;

	return name;
};

export const formatTeamName = (team) => {
	let name = team?.short_name ?? (team?.name ?? false);
	return name;
};

const fakePlayers = (teams) => {
	const fake = {};
	teams.map((team) => {
		team.sections.map((section) => {
			const min = getSectionLimits(section, true).min;

			if(section.candidates == undefined) return;

			let current = 0;
			for(var i = 0; i < min; i++) {
				if(section.candidates[current] == undefined) continue;
				if(fake[section.candidates[current]] != undefined) {
					i--;
					current++;

					// only if the administrator set a lower number of players than possible sections:
					if(current > section.candidates.length) break;

					continue;
				}

				fake[section.candidates[current]] = {
					id:section.candidates[current],
					section_id:section.id,
					team_id:team.id,
				};
				current++;
			}
		});
	});

	return fake;
};

const getAllPlayers = (team) => {
	let data = {};
	team.sections.map((section) => {
		if(section.candidates == undefined) return;

		section.candidates.map((candidate) => {
			data[candidate] = {
				id:candidate,
				section_id:section.id,
				team_id:team.id,
			};
		});
	});

	return data;
};

const hydratePlayers = (players, team, stats = {}, total_users = 1) => {
	const by_section = {};

	team.sections.map(section=>{
		const section_id = section.id;
		if(by_section[section_id] == undefined) {
			if(section != undefined) {
				by_section[section_id] = {
					id:section.id,
					name:section?.name ?? '',
					color:section.color,
					text_color:section.text_color,
					players:[],
				}
			}
		}
	})

	for(var player_id in players) {
		const section_id = players[player_id].section_id;
		const player_stats = (stats[`${player_id}_${section_id}`] != undefined) ? parseInt(stats[`${player_id}_${section_id}`]) : 0;

		const player = team.candidates.find((c) => c.id == player_id);
		if(player != undefined && by_section[section_id] != undefined) {
			by_section[section_id].players.push({
				name:player.name,
				dorsal:player.dorsal,
				percentage:calculatePercentage(player_stats, total_users),
			});
		}
	}

	return Object.values(by_section);
};

const validateTeam = (players, team, intl) => {
	const errors = [];

	const by_section = {};

	for(var player_id in players) {
		const section_id = players[player_id].section_id;
		if(by_section[section_id] == undefined) {
			by_section[section_id] = {
				players:[],
			};
		}
		by_section[section_id].players.push(player_id);
	}

	if(team) {
		(team?.sections ?? []).map((section) => {
			const min = section?.min ?? 1;
			
			if(by_section[section.id] == undefined || by_section[section.id].players.length < min) {
				const section_name = section?.name ?? 'No name';
				errors.push(intl.formatMessage({ id:"The position {position} has not enough players." }, { position:section_name.toUpperCase() }));
			}
		})
	}
	
	return errors;
};

const TeamHeader = ({ team, ...props }) => {
	const is_short = props?.is_short ?? false;

	const has_shield = (team.full_team_image != undefined && team.full_team_image != "") ? true : false;
	const has_name = (team.name != undefined && team.name != "") ? true : false;

	if(!has_shield && !has_name) return null;

	return (
		<header class={`team_header${(!has_shield) ? ' only_name' : ''}`}>
			{(has_shield) && (
				<img
					src={team.full_team_image}
					height="100%"
					alt={team.name}
				/>
			)}

			{(has_name) && (
				(!is_short) ? (
					<p class="team_name">
						<strong>{team.name}</strong>
					</p>
				) : (
					<p class="team_name is_short">
						<strong>{formatTeamName(team)}</strong>
					</p>
				)
			)}
		</header>
	)
};

const PaintPlayerImage = (props) => {
	const candidate = props?.candidate ?? false;
	if(candidate === false) return null;

	const candidate_image = candidate?.full_image ?? false;

	const section = props?.section ?? false;
	const dorsal = props?.dorsal ?? "PL";
	const player_style = props?.team_player_style ?? default_player_style;

	let image_classes = ['candidate_img'];
	//if(candidate_image !== false) image_classes.push('is_default_img');

	let team_sport_type = props?.team_sport_type ?? 'football';
	switch(player_style) {
		case 'circle':
		case 'rect':
			team_sport_type = player_style;
			break;
	}

	if(player_style == 'none' && candidate_image !== false && candidate.full_image != "") {
		return (
			<div class={image_classes.join(" ")}>
				<img src={candidate.full_image} />
			</div>
		)
	}

	return (
		<div class={image_classes.join(" ")}>
			<DefaultSportsShirt
				id={candidate.id}
				type={team_sport_type}
				dorsal={dorsal}
				color={section.color}
				text_color={section.text_color}
				decoration_img={(candidate_image !== false && candidate.full_image != "") ? candidate_image : section.full_decoration_image}
				paint_dorsal={(candidate_image !== false && candidate.full_image != "") ? false : true}
			/>
		</div>
	)
};

const TeamStats = (props) => {
	const teams = props?.stage?.teams ?? [];
	const localized = teams.map((t) => {
		return {
			...t,
			sections:localizeFields(t.sections,props.locale),
			candidates:localizeFields(t.candidates,props.locale)
		}
	});

	const in_audience = props?.in_audience ?? false;
	const is_promo_text_color_bright = isColorBright(getPromotionTextColor(props.promotion));

	return (
		<div class={`team_stats_container has_${teams.length}_teams`} style={{gridTemplateColumns:`repeat(${teams.length}, 1fr)`}}>
			{localized.map((team, t) => {
				const data = props?.processed_data ?? hydratePlayers(
					props?.players ?? fakePlayers(teams ?? []),
					team,
					props?.result?.team_stats ?? {},
					parseInt(props?.result?.total_stage ?? 1),
				);

				return (
					<div key={`team_${t}`} class={`team_${(t + 1)}`}>
						{(teams.length > 1) && (
							<TeamHeader
								team={team}
								is_short={true}
							/>
						)}

						<div class="team_stats gui_main_text_color_on_bg">
							{data.map((section, s) => {
								if(section.players == undefined || section.players.length == 0) return null;

								const styles = (in_audience === false) ? getDefaultTeamColors(section, props.promotion) : null;
			
								return (
									<div key={`section_${s}`} class="team_section">
										<strong class="section_name">{section.name.toUpperCase()}:</strong>

										<ul class="team_players" style={styles}>
											{section.players.map((player, p) => {
												return (
													<li key={`player_${s}_${p}`} class="player_stats">
														<SingleCandidateWithDorsal
															candidate={player}
															force_dorsal={true}
															paint_stats={true}
															in_audience={in_audience}
															order={p}
															is_promo_text_color_bright={is_promo_text_color_bright}
															paint_percentages={props.paint_percentages}
														/>
													</li>
												)
											})}
										</ul>
									</div>
								)
							})}
						</div>
					</div>
				)
			})}
		</div>
	)
};

const PlayersListInPositionInStats = ({ players, section, ...props }) => {
	if(players == undefined || players.length == 0) return null;

	const limit_pre_expand = 10;
	const is_expandable = (players.length > limit_pre_expand) ? true : false;

	const [ is_expanded, setIsExpanded ] = useState((is_expandable) ? false : true);

	const is_promo_text_color_bright = isColorBright(getPromotionTextColor(props.promotion));
	const styles = getDefaultTeamColors(section, props.promotion);

	return (
		<div class="team_section">
			<strong class="section_name">{section.name.toUpperCase()}:</strong>

			<ul class="team_players" style={styles}>
				{players.map((player, p) => {
					if(!is_expanded && p > limit_pre_expand - 1) return null;

					return (
						<li key={`player_${section.id}_${p}`} class="player_stats">
							<SingleCandidateWithDorsal
								candidate={player}
								force_dorsal={true}
								paint_stats={true}
								order={p}
								is_promo_text_color_bright={is_promo_text_color_bright}
								paint_percentages={props.paint_percentages}
							/>
						</li>
					)
				})}

				{(is_expandable && !is_expanded) && (
					<li class="view_all">
						<ActionLink onClick={() => setIsExpanded(true)}>View all</ActionLink>
					</li>
				)}
			</ul>
		</div>
	)
}

const TeamStatsPage = (props) => {
	const teams = props?.stage?.teams ?? [];

	const localized = teams.map((t) => {
		return { ...t, sections:localizeFields(t.sections, props.locale) };
	});

	const stats = props?.stats ?? false;
	if(stats === false) return null;

	let teams_info = [];
	localized.map((team) => {
		teams_info.push(props?.processed_data ?? hydratePlayers(
			getAllPlayers(team),
			team,
			stats?.team ?? {},
			parseInt(props?.result?.total_stage ?? 1),
		));
	});

	return (
		<div class={`team_stats_container all_stats has_${teams.length}_teams`} style={{gridTemplateColumns:`repeat(${teams.length}, 1fr)`}}>
			{teams_info.map((team_data, t) => {
				const team = localized[t];
				const sorted_team = sortPlayersInPosition(team, 'percentage', stats);
				
				return (
					<div key={`team_${t}`} class={`team_${(t + 1)}`}>
						{(teams_info.length > 1) && (
							<TeamHeader
								team={team}
								is_short={true}
							/>
						)}

						<div class="team_stats gui_main_text_color_on_bg">
							{team_data.map((section, s) => {
								return (
									<PlayersListInPositionInStats
										key={`section_${t}_${s}`}
										players={sorted_team[section.id]}
										section={section}
										promotion={props.promotion}
										paint_percentages={props.paint_percentages}
									/>
								)
							})}
						</div>
					</div>
				)
			})}
		</div>
	)
};

const ShareThis = ({ stage, promotion, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_builder = props?.is_builder ?? false;

	if(is_builder) {
		return (
			<div class="share_btn gui_big_button flex_button">
				<ActionButton
					_fullclass=""
					onClick={() => { alert("This will open the Share dialog")}}
				>{stage?.button_label ?? i18n("Share")}</ActionButton>
			</div>
		)
	}

	const locale = (props.locale == undefined) ? '' : '?lc='+props.locale;
	const result = props?.result ?? false;

	const participation_hash = md5(`${result.uid}EPTEAM`).slice(0, 6);
	const participation_id = result.uid ?? 'test';

	const domain_url = window?.domain_url ?? (window?.common?.domain ?? window.location.hostname);
	const share_url_base = (!is_builder) ? `https://${domain_url}/` : window.public_url;

	const share_title = stage?.share_title ?? i18n("This is my team");
	const share_description = stage?.share_description ?? i18n("You can create your own!");
	const share_tweet = stage?.share_tweet ?? (promotion?.post_tweet ?? share_title);
	const share_instant = stage?.share_instant ?? (promotion?.post_instant ?? share_title);

	const image = result?.team_image ?? false;

	const share_layer_props = {
		is_builder,
		promotion,
		info:{
			url:`${share_url_base}share/${participation_id}/${participation_hash}${locale}`,
			image:image,
			title:share_title,
			descriptionenc:share_description,
			postenc:encodeURI(share_tweet),
			instantenc:encodeURI(share_instant),
			urlencodeparameter:'',
		},
		is_mobile:(!isDesktop()) ? true : false,
		enable_behavior:(is_builder) ? true : null,
		layer_title:i18n(props?.layer_title ?? "Share your team"),
		sharing_type:'team',
		sharing_page:'team',
	};

	return (
		<ShareButton
			{...share_layer_props}
			icon={false}
			{...props.custom_cta_props}
		>{stage?.button_label ?? i18n("Share")}</ShareButton>
	)
};

export const TeamSuccess = ({ stage, ...props }) => {
	useCSS('comp/game_teams.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_builder = props?.is_builder ?? false;
	const result = props?.result ?? false;
	const promotion = props?.promotion ?? false;

	const main_description_placeholder = `<h1>${i18n("This is your winning team!")}</h1><p>${i18n("Share it now with your friends so they can also create their own and take part in the competition.")}</p>`;

	let main_description = stage?.main_description ?? "";
	if(main_description == "") main_description = main_description_placeholder;

	let image_styles = { '--main-color':'var(--secondary-color)' };
	let image = result?.team_image ?? false;
	if(is_builder) {
		image = (stage.bg_image != undefined && stage.bg_image != '') ? (stage?.full_bg_image ?? false) : false;
		if(image === false) image = `${window.cdn_url}img/teams/${stage?.team_sport_type ?? 'football'}_field.jpg`;
	}

	let image_classes = ['team_image'];
	if(is_builder) image_classes.push('gui_remove_pointer_events demo_image');

	return (
		<div class="team_root team_success">
			{(image !== false) && (
				<div class={image_classes.join(" ")}>
					<ActionLink
						_href={image}
						_target='_blank'
						_class={{other:'image_link'}}
						style={image_styles}
					>
						<img src={image} width="100%" alt="" />
					</ActionLink>

					<div class="team_image_actions success_actions gui_main_text_color_on_bg">
						<div class="download_btn">
							{(!is_builder) ? (
								<ActionLink
									_href={image}
									_target='_blank'
									icon='knk-download'
									download='my-team.jpg'
								>Download</ActionLink>
							) : (
								<ActionLink
									onClick={() => { alert("This will download the image automatically")}}
									icon='knk-download'
								>Download</ActionLink>
							)}
						</div>
					</div>
				</div>
			)}

			<div class="team_main_info">
				<article class="gui_main_text_color_on_bg">
					<LeveledHTMLText>{main_description}</LeveledHTMLText>
				</article>
			</div>

			<article class="team_share template_module info_flexwrap gui_main_actions">
				<div class="template_submodule call_to_action_flex enter_flexwrap">
					<ShareThis
						stage={stage}
						locale={props.locale}
						promotion={promotion}
						result={result}
						is_builder={is_builder}
						intl={props.intl}
					/>
				</div>
			</article>

			<TeamStats
				stage={stage}
				promotion={promotion}
				locale={props.locale}
				players={(!is_builder) ? props.players : null}
				result={result}
				intl={props.intl}
			/>

			<div class="continue_btn">
				<ActionLink
					_href={result.url}
					autotranslate={false}
				>{stage?.secondary_button_label ?? i18n("Finalize")}</ActionLink>
			</div>
		</div>
	)
};

export const TeamFinalWinner = ({ stage, ...props }) => {
	useCSS('comp/game_teams.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const result = props?.result ?? false;
	const promotion = props?.promotion ?? false;

	const main_description_placeholder = `<h1>${i18n("Winner team")}</h1>`;

	let main_description = stage?.team_results_description ?? "";
	if(main_description == "") main_description = main_description_placeholder;

	let image_styles = { '--main-color':'var(--secondary-color)' };
	let image = stage?.full_team_result_image ?? false;

	let image_classes = ['team_image'];

	const stats = useTeamStats(promotion.id, stage.id);

	return (
		<div class="team_root team_success">
			{(image !== false) && (
				<div class={image_classes.join(" ")}>
					<ActionLink
						_href={image}
						_target='_blank'
						_class={{other:'image_link'}}
						style={image_styles}
					>
						<img src={image} width="100%" alt="" />
					</ActionLink>
				</div>
			)}

			<div class="team_main_info">
				<article class="gui_main_text_color_on_bg">
					<LeveledHTMLText
						_class={{ other:'promotion_title' }}
					>{main_description}</LeveledHTMLText>
				</article>
			</div>

			<TeamStats
				stage={stage}
				promotion={promotion}
				locale={props.locale}
				players={stage.team_result}
				result={{team_stats:stats.team,total_stage:stats.total}}
				intl={props.intl}
			/>
		</div>
	)
};

export const TeamPublicStats = ({ stage, promotion, ...props }) => {
	useCSS('comp/game_teams.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const main_stats_placeholder = `<h1>${i18n("Statistics")}</h1>`;

	const is_builder = props?.is_builder ?? false;

	let main_description = stage?.team_stats_description ?? "";
	if(main_description == "") main_description = main_stats_placeholder;

	const team = props?.team ?? {};

	const stats = useTeamStats(stage.promotion_id, stage.id);

	return (
		<div class="team_root team_public_stats">
			<article class="team_description gui_main_text_color_on_bg">
				<LeveledHTMLText
					_class={{ other:'fill_description' }}
				>{main_description}</LeveledHTMLText>
			</article>

			<TeamStatsPage
				stage={stage}
				promotion={promotion}
				//locale={props.locale}
				players={(!is_builder) ? (props?.players ?? {}) : null}
				stats={stats}
				intl={props.intl}
			/>
		</div>
	)
};

export const TeamPublicShare = ({ stage, ...props }) => {
	useCSS('comp/game_teams.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const user = props?.user ?? false;
	const promotion = props?.promotion ?? false;
	const is_builder = props?.is_builder ?? false;

	const main_shared_placeholder = `<h1>${i18n("The team of {name}")}</h1><p>${i18n("Want to take part in the competition? Build your own team now in less than 2 minutes.")}</p>`;

	let user_name = i18n("Player");
	if(!is_builder) {
		user_name = (promotion.need_nickname != undefined && promotion.need_nickname == '1') ? user.name : user.first_name;
	}

	let main_description = stage?.team_shared_description ?? "";
	if(main_description == "") main_description = main_shared_placeholder;

	let button_cta = stage?.team_shared_button_label ?? "";
	if(button_cta == "") button_cta = i18n("Build my team");

	let image = user?.quiz_entry?.full_image ?? false;
	if(is_builder) {
		image = (stage.bg_image != undefined && stage.bg_image != '') ? (stage?.full_bg_image ?? false) : false;
		if(image === false) image = `${window.cdn_url}img/teams/${stage?.team_sport_type ?? 'football'}_field.jpg`;
	}

	let image_classes = ['team_image'];
	if(is_builder) image_classes.push('gui_remove_pointer_events demo_image');

	return (
		<div class="team_root team_shared">
			<article class="team_description gui_main_text_color_on_bg">
				<LeveledHTMLText
					_class={{ other:'fill_description' }}
				>{main_description.replace("{name}", user_name)}</LeveledHTMLText>
			</article>

			{(image !== false) && (
				<div class={image_classes.join(" ")}>
					<ActionLink _href={image} _target='_blank'>
						<img src={image} width="100%" alt="" />
					</ActionLink>
				</div>
			)}

			<article class="team_cta template_module info_flexwrap gui_main_actions">
				<div class="template_submodule call_to_action_flex enter_flexwrap">
					<div class="enter flex_button gui_big_button register_flex">
						<ActionButton
							_fullclass=""
							_href={`/p/${promotion.id}`}
							autotranslate={false}
						>{button_cta}</ActionButton>
					</div>
				</div>
			</article>
		</div>
	)
};

export const SingleCandidate = ({ candidate, ...props }) => {
	useCSS('comp/game_teams.css');

	const section = props?.section ?? {};
	const promotion = props?.promotion ?? {};
	const user = props?.user ?? {};
	const stage = props?.stage ?? {};
	const total_members_in_team = props?.total_members_in_team ?? 0;
	const max_in_team = props?.max_in_team ?? 11;
	const only_list = props?.only_list ?? false;
	const display_images = props?.display_images ?? true;

	const members_in_section = Object.values(user).filter((m) => (m.section_id == section.id)).length;

	let is_in_other_section = false;
	let status = '';
	if(!only_list) {
		if(user[candidate.id] != undefined && user[candidate.id].section_id == section.id) {
			status = 'selected';
		} else if(user[candidate.id] != undefined || members_in_section == section.max || max_in_team == total_members_in_team) {
			status = 'disabled';
			if(user[candidate.id] != undefined) is_in_other_section = true;
		} else {
			status = 'active';
		}
	} else {
		status = 'only_list';
	}

	return React.cloneElement(props.children, { ...props, candidate, section, promotion, status, stage, is_in_other_section, display_images });
};

const StatsBar = ({ percent, ...props }) => {
	const [ is_loaded, setIsLoaded ] = useState(false);

	const order = props?.order ?? false;

	useEffect(() => {
		setTimeout(() => {
			setIsLoaded(true);
		}, ((order !== false) ? (order * 300) : (Math.random() * 1000)));
	}, []);

	const paint_stats = props?.paint_stats ?? false;
	const is_promo_text_color_bright = props?.is_promo_text_color_bright ?? false;

	if(!paint_stats || parseFloat(percent) == 0) return null;

	let main_classes = ['stats_bar'];
	if(is_loaded) main_classes.push('is_loaded');

	return (
		<span class={main_classes.join(" ")}>
			<svg class="bar">
				<g class="bar_contents">
					<line
						x1="0"
						y1="50%"
						x2={percent}
						y2="50%"
					/>

					<line
						class={`color_modifier is_${(!is_promo_text_color_bright) ? 'light' : 'dark'}`}
						x1="0"
						y1="50%"
						x2={percent}
						y2="50%"
					/>
				</g>
			</svg>
		</span>
	)
};

const SingleCandidateWithDorsal = ({ candidate, ...props }) => {
	const has_percentages = props?.paint_percentages ?? true;
	const force_dorsal = props?.force_dorsal ?? false;
	const in_audience = props?.in_audience ?? false;
	const is_promo_text_color_bright = props?.is_promo_text_color_bright ?? false;

	const has_dorsal = (force_dorsal || (candidate.dorsal != undefined && candidate.dorsal != "")) ? true : false;

	const text_color = (!is_promo_text_color_bright) ? '0, 0, 0, 1' : '255, 255, 255, 1';

	return (
		<>
			<StatsBar
				percent={candidate.percentage}
				paint_stats={props.paint_stats}
				order={props.order}
				is_promo_text_color_bright={is_promo_text_color_bright}
			/>

			{(has_dorsal) && (
				<span class="dorsal">
					<span class={`original_color_item${(in_audience) ? ' whiteColor' : ''}`}>{getPlayerDorsal(candidate)}</span>
				</span>
			)}

			<div class={`name_dorsal${(!has_dorsal) ? ' only_name' : ''}`} style={{'--text-color':text_color}}>
				<strong class="name">
					<span class="gui_cutTextOnOverflow">{candidate.name}</span>
				</strong>

				{(has_percentages) && (
					<span class="stat">{candidate.percentage}</span>
				)}
			</div>
		</>
	)
};

export const SingleCandidateOnly = ({ candidate, section, ...props }) => {
	useCSS('comp/game_teams.css');

	const status = props?.status ?? 'only_list';
	const display_images = props?.display_images ?? true;

	let classes = ['single_candidate', status];

	const player_name = candidate?.name ?? "Player name";

	return (
		<li class={classes.join(" ")}>
			{(display_images) && (
				<PaintPlayerImage
					candidate={candidate}
					section={section}
					team_sport_type={props?.stage?.team_sport_type ?? 'football'}
					team_player_style={props?.stage?.team_player_style ?? default_player_style}
					dorsal={getPlayerDorsal(candidate)}
				/>
			)}

			<div class="candidate_name">
				<span>
					<span class="original_color_item">{player_name}</span>
				</span>
			</div>
		</li>
	)
};

const SingleCandidateResultPicker = ({ candidate, section, ...props }) => {
	const status = props?.status ?? 'active';
	const is_in_other_section = props?.is_in_other_section ?? false;

	let classes = ['single_candidate', 'team_picking', status];
	const key = `${candidate.id}_${section.id}`;
	const percentage = props.stats ? props.stats.team[key] ?? 0 : 0;
	const total = props.stats ? props.stats.total : 1;

	return (
		<li
			class={classes.join(" ")}
			onClick={(status != 'disabled') ? () => props.select(section.id, candidate.id) : () => props.displayErrorLayer(is_in_other_section)}
		>
			<SingleCandidateWithDorsal
				candidate={{...candidate, percentage:calculatePercentage(percentage, total)}}
			/>
		</li>
	)
};

const SingleCandidateContents = ({ candidate, section, ...props }) => {
	const status = props?.status ?? 'active';
	const is_in_other_section = props?.is_in_other_section ?? false;
	const display_images = props?.display_images ?? true;
	
	let styles = (status == 'selected') ? getDefaultTeamColors(section, props.promotion) : {};

	const classes = ['single_candidate', status];
	return (
		<li
			class={classes.join(" ")}
			onClick={(status != 'disabled') ? () => props.select(section.id, candidate.id) : () => props.displayErrorLayer(is_in_other_section)}
			style={styles}
		>
			{(display_images) && (
				<div class="candidate_container">
					<PaintPlayerImage
						candidate={candidate}
						section={section}
						team_sport_type={props?.stage?.team_sport_type ?? 'football'}
						team_player_style={props?.stage?.team_player_style ?? default_player_style}
						dorsal={getPlayerDorsal(candidate)}
					/>
				</div>
			)}

			<div class="candidate_name">
				<span>
					<span class="original_color_item">{candidate.name}</span>
				</span>
			</div>
		</li>
	)
};

export const UserTeamParticipationDetail = (props) => {
	useCSS('comp/game_teams.css');

	const team = props?.team ?? false;
	if(team === false) return null;
	const image = props?.full_image ?? false;
	const display_type = props?.display_type ?? 'detail';
	let image_styles = { '--main-color':'var(--secondary-color)' };
	const stats = useTeamStats(props.stage.promotion_id, props.stage.id);
	let image_classes = ['team_image'];
	return (
		<div class={`team_root team_success in_${display_type}`}>
			{(image !== false) && (
				<div class={image_classes.join(" ")}>
					<ActionLink
						_href={image}
						_target='_blank'
						_class={{other:'image_link'}}
						style={image_styles}
					>
						<img src={image} width="100%" alt="" />
					</ActionLink>

					<div class="team_image_actions in_layer">
						<ActionLink
							_href={image}
							icon='knk-download'
							download='my-team.jpg'
							_target="_blank"
						>Download</ActionLink>

						{(display_type == 'detail') && (
							<ShareThis
								locale={props.locale}
								stage={props.stage}
								promotion={props.promotion}
								result={{
									team_image:image,
									uid:props.user_id,
								}}
								custom_cta_props={{
									cta_type:'link',
									icon:'knk-share',
								}}
								layer_title={props.layer_title}
								intl={props.intl}
							/>
						)}
					</div>
				</div>
			)}

			{(display_type == 'audience') && stats != undefined && (
				<TeamStats
					players={props?.team ?? null}
					stage={props.stage}
					result={{team_stats:stats.team,total_stage:stats.total}}
					paint_percentages={true}
					in_audience={true}
					intl={props.intl}
				/>
			)}
		</div>
	)
};

const PlayersPosition = ({ order, section, ...props }) => {
	const autocollapse_positions = props?.autocollapse_positions ?? false;
	const force_unfold = props?.force_unfold ?? false;
	const _id = props?.id ?? false;
	
	const [ is_folded, setIsFolded ] = useState((autocollapse_positions && order != 0 && !force_unfold) ? true : false);

	useEffect(() => {
		if(props.force_unfold != undefined && props.force_unfold) {
			setIsFolded(false);
		}
	}, [props.force_unfold]);

	useEffect(() => {
		if(props.autocollapse_positions != undefined && !parseBool(props.autocollapse_positions)) {
			setIsFolded(false);
		} else if(order != 0) {
			setIsFolded(true);
		}
	}, [props.autocollapse_positions]);

	let title_classes = ['title', 'original_color_item'];
	let title_icon = null;
	if(autocollapse_positions) {
		title_classes.push('is_autocollapsable');
		if(is_folded) title_classes.push('is_folded');
		title_icon = 'is_icon knk-arrow-b-big';
	}

	return (
		<li>
			{(_id !== false) && (
				<a id={_id} style={{position:'absolute', top:'-4em'}} />
			)}

			<div class={title_classes.join(" ")}>
				<h3 class={title_icon}>{section.name}</h3>

				<span class="hb_mainFontWeightRoman">{getSectionLimits(section, false, props.intl)}</span>

				{(autocollapse_positions) && (
					<ActionLink
						_class={{other:'is_fullscreen relative_to_container'}}
						onClick={() => setIsFolded(!is_folded)}
					/>
				)}
			</div>

			{(!is_folded) && (
				props.children
			)}
		</li>
	)
};

const filterCandidates = (candidate, search) => {
	let regex = new RegExp(search, 'i');
	return regex.test(candidate.name + candidate.dorsal);
};

const TeamClient = ({ stage, team, ...props }) => {
	useCSS('comp/game_teams.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const has_sections = (team?.sections && team.sections.length > 0) ? true : false;
	const num_players = team?.num_players ?? 11;
	const is_builder = props?.is_builder ?? false;
	const only_list = props?.only_list ?? false;
	const promotion = props?.promotion ?? {};
	const display_images = (team.candidates.length > 100) ? false : true;
	const autocollapse_positions = parseBool(team?.autocollapse_positions ?? false);
	
	const enable_search = false;

	const [ user, setUser ] = useState(props?.players ?? {});
	const [ display_error_layer, setDisplayErrorLayer ] = useState(false);
	const [ display_already_layer, setDisplayAlreadyLayer ] = useState(false);
	const [ sorted, setSorted ] = useState(false);
	const [ candidates, searchCandidates ] = useSimpleFilter(team.candidates, filterCandidates);
	const [ actual_position, setActualPosition ] = useState(0);

	useEffect(() => {
		if(!props.players) return;

		const players_in_team = {};
		for (var id in props.players) {
			if (props.players[id].team_id != team.id) continue;
			players_in_team[id] = props.players[id];
		}

		setUser(players_in_team);
	}, [props.players]);

	const select = (section_id, candidate_id) => {
		if(user[candidate_id] == undefined) {
			user[candidate_id] = { id:candidate_id, section_id,team_id:team.id };
		} else {
			delete(user[candidate_id]);
		}
		if(props.onSelect != undefined) props.onSelect({...user});
		setUser({...user});
	};

	const displayErrorLayer = (is_in_other_section) => {
		if(is_in_other_section) {
			setDisplayAlreadyLayer(true);
		} else {
			setDisplayErrorLayer(true);
		}
	};

	const errors = validateTeam(user, team, props.intl);
	const max_in_team = team?.num_players;
	const total_members_in_team = Object.keys(user).length;

	useEffect(() => {
		if(!props.stats) return;
		if(!result_pick) return;

		setSorted(sortPlayersInPosition(team, props.candidate_order, props.stats));
	}, [props.stats, props.candidate_order]);

	if(only_list) {
		return (
			<section class="team_configuration only_list">
				<ul class="candidates_in_section">
					{team.candidates.map((candidate, can) => {
						return (
							<SingleCandidate
								key={`candidate_${can}`}
								candidate={candidate}
								section={{}}
								stage={stage}
								promotion={promotion}
								user={user}
								only_list={true}
							>
								<SingleCandidateOnly />
							</SingleCandidate>
						)
					})}
				</ul>
			</section>
		)
	}

	const cta_disabled = (total_members_in_team != num_players || errors.length > 0) ? true : false;
	const result_pick = props?.result_pick ?? false;

	if(result_pick) {
		const elements = stage.teams.reduce((prev, current) => {
			return prev + parseInt(current.num_players);
		}, 0);

		const current_elements = (props.players) ? Object.keys(props.players).length : 0;

		let display_confirm = false;
		if(stage.teams.length > 1) {
			if(current_elements != elements) display_confirm = true;
		} else {
			if(stage.team_result == undefined || stage.team_result == '') display_confirm = true;
		}

		return (
			<>
				<ul class="team_sections">
					{team.sections.map((section, s) => {
						return (
							<li key={`section_${s}`}>
								<h3 class="title original_color_item">
									{section.name} <span class="hb_mainFontWeightRoman">{getSectionLimits(section, false, props.intl)}</span>
								</h3>

								<ul class="candidates_in_section team_players" style={{gridTemplateColumns:'1fr'}}>
									{sorted && sorted[section.id] != undefined && sorted[section.id].map((candidate, can) => {
										return (
											<SingleCandidate
												key={`candidate_${s}_${can}`}
												candidate={candidate}
												section={section}
												user={user}
												stats={props.stats}
												stage={stage}
												candidate_id={`candidate_${s}_${can}`}
												total_members_in_team={total_members_in_team}
												max_in_team={max_in_team}
												select={(display_confirm) ? select : null}
												displayErrorLayer={displayErrorLayer}
											>
												<SingleCandidateResultPicker />
											</SingleCandidate>
										)
									})}
								</ul>
							</li>
						)
					})}
				</ul>

				<ButtonCollection _class={{other:'gui_flex_space_between'}}>
					<SaveButton
						onClick={(!cta_disabled) ? (e, cb) => props.next(user, cb) : null}
						autotranslate={false}
						disabled={cta_disabled}
					>{i18n("Confirm")} ({total_members_in_team}/{num_players})</SaveButton>
				</ButtonCollection>

				{(display_error_layer) && (
					<FloatingLayer _class={{size:'small'}} backButton={() => setDisplayErrorLayer(false)}>
						<div class="without_actions">
							<SectionTitle>Oops</SectionTitle>

							<p>{i18n("It is not possible to select more players for this position.")}</p>
						</div>
					</FloatingLayer>
				)}

				{(display_already_layer) && (
					<FloatingLayer _class={{size:'small'}} backButton={() => setDisplayAlreadyLayer(false)}>
						<div class="without_actions">
							<SectionTitle>Oops</SectionTitle>

							<p>{i18n("The player is already selected in another position.")}</p>
						</div>
					</FloatingLayer>
				)}
			</>
		)
	}

	const goToNextPosition = (next_position) => {
		setActualPosition((parseInt(next_position) == next_position) ? Math.max(actual_position, next_position) : next_position);

		const next_id = (next_position != 'button') ? `section_${next_position}` : 'team_button';
		setTimeout(() => {
			document.querySelector(`#${next_id}`).scrollIntoView({
				behavior:'smooth',
			});
		}, 200);
	};

	const localized_sections = localizeFields(team.sections,props.locale);
	const localized_candidates = localizeFields(team.candidates,props.locale);

	return (
		<section class="team_root team_configuration gui_main_text_color_on_bg">
			<TeamHeader
				team={team}
			/>

			{(enable_search && !display_images) && (
				<SearchFeld
					name="search"
					placeholder="Search..."
					change={(k, v) => searchCandidates(v)}
				/>
			)}

			{(has_sections) && (
				<ul class="team_sections">
					{localized_sections.map((section, s) => {
						const candidates_avail = localized_candidates.filter((c) => ((section?.candidates ?? []).includes(c.id)));
						if(candidates_avail.length == 0) return null;

						const members_in_section = Object.values(user).filter((m) => (m.section_id == section.id)).length;

						const section_id = `section_${s}`;

						if(actual_position == s && members_in_section == section.max) {
							goToNextPosition((s < localized_sections.length - 1) ? (s + 1) : 'button');
						}
					
						return (
							<PlayersPosition
								key={section_id}
								id={section_id}
								order={s}
								section={section}
								autocollapse_positions={autocollapse_positions}
								intl={props.intl}
								force_unfold={(s <= actual_position) ? true : false}
							>
								<ul class="candidates_in_section">
									{candidates_avail.map((candidate, can) => {
										return (
											<SingleCandidate
												key={`candidate_${s}_${can}`}
												candidate={candidate}
												section={section}
												promotion={promotion}
												user={user}
												stage={stage}
												only_list={only_list}
												candidate_id={`candidate_${s}_${can}`}
												total_members_in_team={total_members_in_team}
												max_in_team={max_in_team}
												select={select}
												displayErrorLayer={displayErrorLayer}
												display_images={display_images}
											>
												<SingleCandidateContents />
											</SingleCandidate>
										)
									})}
								</ul>
							</PlayersPosition>
						)
					})}
				</ul>
			)}

			<div class="template_submodule call_to_action_flex enter_flexwrap">
				<div id="team_button" class={`enter flex_button gui_big_button register_flex gui_not_in_top_huge gui_visibled_overflowed cta_with_error_feedback${(cta_disabled) ? ' gui_remove_pointer_events' : ''}`}>
					{(total_members_in_team == max_in_team && errors.length > 0) && (
						<div class="team_errors hb_secondaryFont">
							<ul>
								{errors.map((error, e) => (
									<li key={`error_${e}`} class="knk-alert gui_small_icon">
										{error}
									</li>
								))}
							</ul>
						</div>
					)}

					<SaveButton
						_fullclass=""
						onClick={(!cta_disabled) ? (e, cb) => props.next(user, cb) : null}
						autotranslate={false}
						disabled={cta_disabled}
					>{i18n("Continue")} ({total_members_in_team}/{num_players})</SaveButton>
				</div>
			</div>

			{(display_error_layer && !is_builder) && (
				<FloatingLayerPublic
					pm={promotion}
					title="Oops"
					is_builder={false}
					backButton={() => setDisplayErrorLayer(false)}
				>
					<p>{i18n("It is not possible to select more players for this position.")}</p>
				</FloatingLayerPublic>
			)}

			{(display_already_layer && !is_builder) && (
				<FloatingLayerPublic
					pm={promotion}
					title="Oops"
					is_builder={false}
					backButton={() => setDisplayAlreadyLayer(false)}
				>
					<p>{i18n("The player is already selected in another position.")}</p>
				</FloatingLayerPublic>
			)}
		</section>
	)
}

export default injectIntl(TeamClient);